import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add as AddIcon } from '@mui/icons-material';
import { projectService } from '../../services/project.service';
import { Project } from '../../types/project.types';

const Glyph = ({ icon, size = '1.2rem' }: { icon: string; size?: string }) => (
  <Box
    component="span"
    className="material-symbols-rounded"
    sx={{
      fontSize: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    }}
  >
    {icon}
  </Box>
);

export const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadProjects();
    
    // URL 파라미터에서 status 가져오기
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setStatusFilter(statusParam);
    }
  }, [searchParams]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('프로젝트 로드 실패:', error);
      alert('프로젝트를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.projectNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusConfig = (status: string) => {
    const configs = {
      '계획': {
        label: '계획',
        color: '#FFFFFF',
        bgColor: '#3b82f6',
        icon: 'edit_note'
      },
      '진행중': {
        label: '진행중',
        color: '#FFFFFF',
        bgColor: '#10b981',
        icon: 'play_circle'
      },
      '완료': {
        label: '완료',
        color: '#FFFFFF',
        bgColor: '#6b7280',
        icon: 'check_circle'
      },
      '보류': {
        label: '보류',
        color: '#FFFFFF',
        bgColor: '#f59e0b',
        icon: 'cancel'
      }
    };
    return configs[status as keyof typeof configs] || configs['계획'];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
      {/* 헤더 */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}
          >
            프로젝트 관리
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: alpha(theme.palette.text.secondary, 0.74), mt: 0.5 }}
          >
            진행 중인 프로젝트를 한눈에 확인하세요.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/projects/new')}
          sx={{
            px: 3,
            py: 1.2,
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          프로젝트 등록
        </Button>
      </Stack>

      {/* 검색 및 필터 */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
            theme.palette.primary.dark,
            0.08
          )})`,
          boxShadow: `0 8px 20px ${alpha(theme.palette.primary.dark, 0.15)}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              placeholder="프로젝트 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">전체 상태</MenuItem>
                <MenuItem value="계획">계획</MenuItem>
                <MenuItem value="진행중">진행중</MenuItem>
                <MenuItem value="완료">완료</MenuItem>
                <MenuItem value="보류">보류</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* 프로젝트 카드 리스트 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: { xs: 2, md: 3 },
        }}
      >
        {filteredProjects.map((project) => {
          const statusConfig = getStatusConfig(project.status);
          
          return (
            <Card
              key={project.id}
              elevation={0}
              onClick={() => navigate(`/projects/${project.id}`)}
              sx={{
                cursor: 'pointer',
                border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
                backgroundImage: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.light,
                  0.05
                )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(theme.palette.primary.dark, 0.25)}`,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardContent>
                {/* 프로젝트 헤더 */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      flex: 1,
                      mr: 1,
                    }}
                  >
                    {project.projectName}
                  </Typography>
                  <Chip
                    icon={<Glyph icon={statusConfig.icon} size="1rem" />}
                    label={statusConfig.label}
                    size="small"
                    sx={{
                      backgroundColor: statusConfig.bgColor,
                      color: statusConfig.color,
                      fontWeight: 500,
                    }}
                  />
                </Stack>

                {/* 프로젝트 번호 */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, fontWeight: 500 }}
                >
                  {project.projectNo}
                </Typography>

                {/* 프로젝트 정보 */}
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Chip
                    icon={<Glyph icon="business" size="1rem" />}
                    label={project.customerName}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                  <Chip
                    icon={<Glyph icon="precision_manufacturing" size="1rem" />}
                    label={`금형 ${project.moldCount || 0}개`}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                </Stack>

                {/* 프로젝트 설명 */}
                {project.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.5,
                    }}
                  >
                    {project.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* 빈 상태 메시지 */}
      {filteredProjects.length === 0 && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
            backgroundColor: alpha(theme.palette.primary.light, 0.05),
            textAlign: 'center',
            py: 8,
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                mb: 3,
              }}
            >
              <Glyph icon="folder_off" size="2.5rem" />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              {searchTerm || statusFilter !== 'all'
                ? '검색 결과가 없습니다'
                : '프로젝트가 없습니다'}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              {searchTerm || statusFilter !== 'all'
                ? '다른 검색어나 필터를 시도해보세요.'
                : '새 프로젝트를 등록해보세요.'}
            </Typography>
            {!searchTerm && statusFilter === 'all' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/projects/new')}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                }}
              >
                프로젝트 등록
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
